import React, { useState, useEffect } from 'react';
import { Calendar, Warning, CheckCircle, PencilSimple, X, Download, FileText } from '@phosphor-icons/react';
import axios from '../../utils/axiosInstance';
import { WlasnoscBadge } from '../shared/WlasnoscFilter';

const API = `${window.location.origin}/api`;

// Calculate days until date
const getDaysUntil = (dateString) => {
  if (!dateString) return null;
  const target = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  const diffTime = target - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

// Get status color based on days remaining
const getStatusColor = (dateString) => {
  const days = getDaysUntil(dateString);
  if (days === null) return 'bg-gray-100 text-gray-700';
  if (days < 0) return 'bg-red-100 text-red-800 border-red-300';
  if (days <= 30) return 'bg-yellow-100 text-yellow-900 border-yellow-300';
  return 'bg-green-100 text-green-800 border-green-300';
};

// Get status badge
const getStatusBadge = (dateString) => {
  const days = getDaysUntil(dateString);
  if (days === null) return { text: 'Brak daty', icon: null };
  if (days < 0) return { text: `Przeterminowane (${Math.abs(days)} dni)`, icon: <Warning className="w-4 h-4" weight="bold" /> };
  if (days <= 30) return { text: `Pozostało ${days} dni`, icon: <Warning className="w-4 h-4" /> };
  return { text: `Ważne (${days} dni)`, icon: <CheckCircle className="w-4 h-4" /> };
};

export default function MyVehiclesTab() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editFormData, setEditFormData] = useState({
    ubezpieczenie: '',
    przeglad: ''
  });

  useEffect(() => {
    fetchMyVehicles();
  }, []);

  const fetchMyVehicles = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/terminy/my/vehicles`);
      setVehicles(response.data);
    } catch (err) {
      console.error('Error fetching vehicles:', err);
      alert('Błąd podczas pobierania danych');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (vehicle) => {
    setEditingId(vehicle.id);
    setEditFormData({
      ubezpieczenie: vehicle.ubezpieczenie || '',
      przeglad: vehicle.przeglad || ''
    });
  };

  const handleSaveEdit = async (vehicleId) => {
    try {
      await axios.put(`${API}/terminy/${vehicleId}`, editFormData);
      setEditingId(null);
      fetchMyVehicles();
    } catch (err) {
      console.error('Error updating vehicle:', err);
      const errorMsg = err.response?.data?.detail || 'Błąd podczas zapisywania';
      alert(errorMsg);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditFormData({ ubezpieczenie: '', przeglad: '' });
  };

  const handleDownload = async (vehicle, docType) => {
    try {
      const pojazdId = vehicle.pojazd_id || vehicle.id;
      const response = await axios.get(
        `${API}/pojazdy/${pojazdId}/document?type=${docType}`,
        { responseType: 'blob' }
      );
      const contentType =
        response.headers?.['content-type'] ||
        response.data?.type ||
        'application/octet-stream';
      const extMap = {
        'application/pdf': 'pdf',
        'image/jpeg': 'jpg',
        'image/jpg': 'jpg',
        'image/png': 'png',
      };
      const ext = extMap[contentType.split(';')[0].trim()] || 'bin';
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const prefix = docType === 'ubezpieczenie' ? 'polisa' : 'dowod';
      a.download = `${prefix}_${vehicle.nr_rejestracyjny || vehicle.nazwa_pojazdu || pojazdId}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      alert('Nie udało się pobrać dokumentu');
    }
  };

  if (loading) {
    return <div className="text-center py-12">Ładowanie...</div>;
  }

  if (vehicles.length === 0) {
    return (
      <div className="bg-white rounded-sm shadow-lg p-12 text-center">
        <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-700 mb-2">Brak przypisanych pojazdów</h3>
        <p className="text-gray-500">Administrator jeszcze nie przypisał Ci żadnego pojazdu.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-slate-800">Moje pojazdy</h2>
        <p className="text-slate-600 mt-1">Zarządzaj terminami ubezpieczeń i przeglądów swoich pojazdów</p>
      </div>

      {/* Vehicles Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {vehicles.map((vehicle) => (
          <div key={vehicle.id} className="bg-white rounded-sm shadow-lg border border-gray-100 overflow-hidden">
            <div className="bg-[#0B1E3F] p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                    <Calendar className="w-7 h-7" weight="bold" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <h3 className="text-2xl font-bold">{vehicle.nazwa_pojazdu}</h3>
                    <WlasnoscBadge value={vehicle.wlasnosc} size="xs" />
                  </div>
                </div>
                {editingId !== vehicle.id && (
                  <button
                    onClick={() => handleEdit(vehicle)}
                    className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                    title="Edytuj daty"
                  >
                    <PencilSimple className="w-5 h-5" weight="bold" />
                  </button>
                )}
              </div>
              {(vehicle.marka || vehicle.nr_rejestracyjny || vehicle.karta_paliwowa) && (
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-300">
                  {(vehicle.marka || vehicle.model) && (
                    <span>{[vehicle.marka, vehicle.model].filter(Boolean).join(' ')}</span>
                  )}
                  {vehicle.nr_rejestracyjny && (
                    <span className="font-mono bg-white/15 px-2 py-0.5 rounded">{vehicle.nr_rejestracyjny}</span>
                  )}
                  {vehicle.karta_paliwowa && (
                    <span className="text-xs bg-white/15 px-2 py-0.5 rounded">
                      Karta paliwowa: <span className="font-mono">{vehicle.karta_paliwowa}</span>
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="p-6 space-y-4">
              {/* Ubezpieczenie */}
              <div>
                <label className="text-sm font-semibold text-slate-600 mb-2 block">
                  Ubezpieczenie
                </label>
                {editingId === vehicle.id ? (
                  <input
                    type="date"
                    value={editFormData.ubezpieczenie}
                    onChange={(e) => setEditFormData({ ...editFormData, ubezpieczenie: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <div className="space-y-2">
                    <div className="text-lg font-medium text-slate-700">
                      {vehicle.ubezpieczenie 
                        ? new Date(vehicle.ubezpieczenie).toLocaleDateString('pl-PL')
                        : 'Brak daty'}
                    </div>
                    {vehicle.ubezpieczenie && (
                      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold border ${getStatusColor(vehicle.ubezpieczenie)}`}>
                        {getStatusBadge(vehicle.ubezpieczenie).icon}
                        {getStatusBadge(vehicle.ubezpieczenie).text}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Przegląd */}
              <div>
                <label className="text-sm font-semibold text-slate-600 mb-2 block">
                  Przegląd techniczny
                </label>
                {editingId === vehicle.id ? (
                  <input
                    type="date"
                    value={editFormData.przeglad}
                    onChange={(e) => setEditFormData({ ...editFormData, przeglad: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <div className="space-y-2">
                    <div className="text-lg font-medium text-slate-700">
                      {vehicle.przeglad 
                        ? new Date(vehicle.przeglad).toLocaleDateString('pl-PL')
                        : 'Brak daty'}
                    </div>
                    {vehicle.przeglad && (
                      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold border ${getStatusColor(vehicle.przeglad)}`}>
                        {getStatusBadge(vehicle.przeglad).icon}
                        {getStatusBadge(vehicle.przeglad).text}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Documents (downloadable) */}
              {(vehicle.dokument_url || vehicle.ubezpieczenie_url) && (
                <div className="pt-3 border-t border-gray-100">
                  <label className="text-sm font-semibold text-slate-600 mb-2 block">Dokumenty</label>
                  <div className="flex flex-wrap gap-2">
                    {vehicle.dokument_url && (
                      <button
                        data-testid={`emp-download-dowod-${vehicle.id}`}
                        onClick={() => handleDownload(vehicle, 'dowod')}
                        className="px-3 py-2 bg-muted hover:bg-muted text-foreground rounded-lg text-sm font-semibold flex items-center gap-2 transition-colors border border-border"
                      >
                        <FileText className="w-4 h-4" weight="bold" />
                        Dowód rejestracyjny
                        <Download className="w-4 h-4" weight="bold" />
                      </button>
                    )}
                    {vehicle.ubezpieczenie_url && (
                      <button
                        data-testid={`emp-download-ubezp-${vehicle.id}`}
                        onClick={() => handleDownload(vehicle, 'ubezpieczenie')}
                        className="px-3 py-2 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-lg text-sm font-semibold flex items-center gap-2 transition-colors border border-amber-200"
                      >
                        <FileText className="w-4 h-4" weight="bold" />
                        Polisa ubezpieczeniowa
                        <Download className="w-4 h-4" weight="bold" />
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Edit buttons */}
              {editingId === vehicle.id && (
                <div className="flex gap-2 pt-3">
                  <button
                    onClick={() => handleSaveEdit(vehicle.id)}
                    className="flex-1 px-4 py-2 bg-foreground hover:bg-black text-white rounded-lg font-semibold transition-colors"
                  >
                    Zapisz
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Summary */}
      {vehicles.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-muted rounded-sm p-6 text-white shadow-lg">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle className="w-8 h-8" weight="bold" />
              <h3 className="text-lg font-semibold">Aktualne</h3>
            </div>
            <p className="text-3xl font-bold">
              {vehicles.filter(v => {
                const ub = getDaysUntil(v.ubezpieczenie);
                const pr = getDaysUntil(v.przeglad);
                return (ub && ub > 30) || (pr && pr > 30);
              }).length}
            </p>
          </div>
          
          <div className="bg-muted rounded-sm p-6 text-white shadow-lg">
            <div className="flex items-center gap-3 mb-2">
              <Warning className="w-8 h-8" weight="bold" />
              <h3 className="text-lg font-semibold">Wkrótce wygasają</h3>
            </div>
            <p className="text-3xl font-bold">
              {vehicles.filter(v => {
                const ub = getDaysUntil(v.ubezpieczenie);
                const pr = getDaysUntil(v.przeglad);
                return (ub && ub > 0 && ub <= 30) || (pr && pr > 0 && pr <= 30);
              }).length}
            </p>
          </div>
          
          <div className="bg-signal rounded-sm p-6 text-white shadow-lg">
            <div className="flex items-center gap-3 mb-2">
              <Warning className="w-8 h-8" weight="bold" />
              <h3 className="text-lg font-semibold">Przeterminowane</h3>
            </div>
            <p className="text-3xl font-bold">
              {vehicles.filter(v => {
                const ub = getDaysUntil(v.ubezpieczenie);
                const pr = getDaysUntil(v.przeglad);
                return (ub && ub < 0) || (pr && pr < 0);
              }).length}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
